// Camada de acesso ao catálogo. Lê o catálogo publicado pela API; se falhar, usa os dados demonstrativos.
import * as demo from "@/data/demo/catalog";
import { API_URL } from "@/lib/backend";
import { isSoldOut } from "@/lib/patternAvailability";

const API = API_URL;
const normalize = (p) => ({ slots: [], slotsMode: "extra", images: [], features: [], patterns: [], options: [], collections: [], availability: { type: "a-confirmar" }, ...p });

let state = { categories: demo.categories, patterns: demo.patterns, products: demo.products.map(normalize), collections: [], source: "demo" };
const PREVIEW_KEY = "babybib-preview";

const wantsPreview = () => {
  const flag = new URLSearchParams(window.location.search).get("preview");
  if (flag === "1") sessionStorage.setItem(PREVIEW_KEY, "1");
  if (flag === "0") sessionStorage.removeItem(PREVIEW_KEY);
  return sessionStorage.getItem(PREVIEW_KEY) === "1";
};

const fetchJson = async (url, signal, preview) => {
  const opts = { signal, credentials: preview ? "include" : "same-origin", cache: preview ? "no-store" : "default" };
  let res = await fetch(url, opts);
  if (preview && res.status === 401) {
    await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include", signal });
    res = await fetch(url, opts);
  }
  return res.ok ? res.json() : null;
};

export const getSource = () => state.source;
export const exitPreview = () => {
  sessionStorage.removeItem(PREVIEW_KEY);
  window.location.assign("/?preview=0");
};

const CACHE_KEY = "bb-catalog-v1";
const CACHE_TTL = 20 * 1000;
// Sem `any`, a cópia só vale enquanto fresca; com `any`, serve de reserva se a loja não responder (melhor uma cópia antiga do que o catálogo de demonstração).
const readCache = (any = false) => {
  try {
    const c = JSON.parse(localStorage.getItem(CACHE_KEY) ?? "null");
    return c && (any || Date.now() - c.t < CACHE_TTL) && Array.isArray(c.data?.products) ? c.data : null;
  } catch {
    return null;
  }
};
const writeCache = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), data }));
  } catch {
    // sem armazenamento: só não guarda
  }
};
const applyData = (data, source) => {
  state = { categories: data.categories, patterns: data.patterns, products: data.products.map(normalize), collections: Array.isArray(data.collections) ? data.collections : [], source };
};

export const loadCatalog = async () => {
  if (!API) return state.source;
  // Visitas seguintes: desenha logo com a cópia guardada (menos de 20 s) e atualiza-a em segundo plano.
  const cached = wantsPreview() ? null : readCache();
  if (cached) {
    applyData(cached, "api");
    fetchJson(`${API}/catalog`, undefined, false).then((d) => d?.products && writeCache(d)).catch(() => null);
    return state.source;
  }
  let preview = wantsPreview();
  const valid = (d) => Array.isArray(d?.products) && Array.isArray(d?.categories) && d?.patterns;
  // Até 3 tentativas curtas: um pedido que fica pendurado não deixa a loja sem peças (a segunda tentativa costuma apanhar uma instância desperta).
  for (let attempt = 0; attempt < 3 && state.source === "demo"; attempt++) {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), attempt === 0 ? 4000 : 6000);
    try {
      let data = preview ? await fetchJson(`${API}/admin/catalog/preview`, ctrl.signal, true) : null;
      if (preview && !data) {
        sessionStorage.removeItem(PREVIEW_KEY);
        preview = false;
      }
      data = data ?? (await fetchJson(`${API}/catalog`, ctrl.signal, false));
      if (valid(data)) {
        applyData(data, preview ? "preview" : "api");
        if (!preview) writeCache(data);
      }
    } catch {
      // tenta outra vez
    } finally {
      clearTimeout(timer);
    }
  }
  if (state.source === "demo") {
    const old = readCache(true);
    if (old) applyData(old, "api");
  }
  return state.source;
};

export const imageSrc = (id, width = 400) =>
  id.startsWith("static:") ? `/img/catalog/${id.slice(7)}-${width >= 800 ? 1200 : 400}.webp` : `${API}/images/${id}/${width}.webp`;

// Sobe quando as imagens dos tecidos mudam, para os navegadores não ficarem com as antigas em cache.
const TEXTURES_VERSION = 4;
// Versão lisa do tecido (sem pregas), usada nas pré-visualizações das peças. Padrões carregados pela administradora usam a própria imagem.
export const flatSrc = (texture) =>
  texture?.startsWith("img:") ? imageSrc(texture.slice(4), 1200) : `/img/demo/${texture ?? "linho"}-flat-800.webp?v=${TEXTURES_VERSION}`;
// Os tecidos da loja só existem em 240, 400 e 800: qualquer outro tamanho cai no mais próximo (um ficheiro em falta devolveria a página inicial em vez da imagem).
const demoSize = (size) => (size <= 240 ? 240 : size <= 400 ? 400 : 800);
export const textureSrc = (texture, size = 800) =>
  texture?.startsWith("img:") ? imageSrc(texture.slice(4), size >= 800 ? 1200 : 400) : `/img/demo/${texture ?? "linho"}-${demoSize(size)}.webp?v=${TEXTURES_VERSION}`;

export const getCategories = () => state.categories.filter((c) => state.products.some((p) => p.category === c.id));
export const getCategory = (id) => state.categories.find((c) => c.id === id) ?? null;
export const getProducts = (categoryId) => (categoryId ? state.products.filter((p) => p.category === categoryId) : state.products);
export const getProduct = (slug) => state.products.find((p) => p.slug === slug) ?? null;
export const getFeatured = () => state.products.filter((p) => p.featured);
export const getPattern = (id) => (state.patterns[id] ? { id, ...state.patterns[id] } : null);
export const getProductPatterns = (product) => (product?.patterns ?? []).map(getPattern).filter(Boolean);
// Padrões que se podem escolher (os esgotados continuam visíveis, mas não se escolhem).
export const firstChoosable = (list) => list.find((p) => !isSoldOut(p)) ?? null;
// Todos os padrões de uma linha do carrinho: o da peça e os das partes (inclui os sugeridos).
export const itemPatterns = (item) => [item?.patternId, ...Object.values(item?.slots ?? {}).map((v) => v?.patternId)].filter((id, i, a) => id && a.indexOf(id) === i).map(getPattern).filter(Boolean);
export const getPatterns = () => Object.keys(state.patterns).map(getPattern);
export const countByPattern = (id) => state.products.filter((p) => p.patterns.includes(id)).length;
export const getFaqs = () => demo.faqs;
export const countByCategory = (id) => state.products.filter((p) => p.category === id).length;

export const getProductsWithPattern = (patternId, excludeSlug) =>
  state.products.filter((p) => p.slug !== excludeSlug && p.patterns.includes(patternId));

export const maxQuantity = (product) =>
  product?.availability?.type === "stock" ? Math.max(product.availability.quantity ?? 0, 0) : 10;

// Fotografias reais do produto: primeiro as do padrão escolhido, depois as gerais.
export const productImages = (product, patternId) => {
  const imgs = Array.isArray(product?.images) ? product.images : [];
  const own = imgs.filter((i) => i.patternId === patternId);
  const general = imgs.filter((i) => !i.patternId);
  const picked = [...own, ...general];
  return picked.length ? picked : imgs;
};

// Partes com padrão próprio (elástico, babetes de um pack). Sem lista própria, vale a de padrões da peça.
export const isPack = (product) => product?.slotsMode === "pack" && (product?.slots?.length ?? 0) > 0;
export const slotPatterns = (product, slot) => {
  const ids = slot?.patterns?.length ? slot.patterns : product?.patterns ?? [];
  return ids.map(getPattern).filter(Boolean);
};
// Parte sugerida (ex.: a fita): o padrão vem da combinação que a Alexandra definiu para o padrão da peça (sem combinação, o mesmo padrão).
export const suggestedPatternId = (slot, mainPatternId) => slot?.fixedPatternId ?? slot?.suggestions?.find((x) => x.patternId === mainPatternId)?.slotPatternId ?? mainPatternId;
// Partes que só existem com uma escolha (ex.: o verso do babete reversível, só com "Reversível: Sim"). Seleção: { [optionId]: { choiceId } }.
export const slotActive = (slot, selection) => !slot?.onlyWith || selection?.[slot.onlyWith.optionId]?.choiceId === slot.onlyWith.choiceId;
export const activeSlots = (product, selection) => (product?.slots ?? []).filter((s) => slotActive(s, selection));
export const hasSuggested = (product) => (product?.slots ?? []).some((s) => s.suggested);

// Escolhas iniciais: nos packs cada parte começa com um padrão e um modelo diferentes; no elástico, o padrão da peça.
export const defaultSlots = (product, mainPatternId) =>
  Object.fromEntries((product?.slots ?? []).map((s, i) => {
    const list = slotPatterns(product, s);
    const start = Math.max(0, list.findIndex((p) => p.id === mainPatternId));
    const pickFrom = (i) => { const n = Math.max(list.length, 1); for (let k = 0; k < n; k++) { const c = list[(i + k) % n]; if (c && !isSoldOut(c)) return c; } return list[i % n]; };
    // Uma parte com lista própria de padrões começa pelo primeiro; num pack, cada parte começa com um padrão diferente.
    if (s.suggested) return [s.id, { modelId: null, patternId: suggestedPatternId(s, mainPatternId) }];
    const own = s.patterns?.length > 0;
    // Uma parte que só existe com uma escolha (o verso) começa noutro padrão que não o da peça: é para ter dois padrões diferentes.
    if (s.onlyWith) return [s.id, { modelId: null, patternId: (pickFrom(start + 1) ?? pickFrom(0))?.id ?? null }];
    const pattern = own ? pickFrom(0) : isPack(product) ? pickFrom(start + i * 7) : list.find((p) => p.id === mainPatternId && !isSoldOut(p)) ?? pickFrom(0);
    return [s.id, { modelId: s.models?.length ? s.models[i % s.models.length].id : null, patternId: pattern?.id ?? null }];
  }));
export const slotLines = (product, slots) =>
  (product?.slots ?? []).flatMap((s) => {
    const v = slots?.[s.id];
    if (!v) return [];
    return [{ label: s.label, model: s.models?.find((m) => m.id === v.modelId)?.label ?? null, pattern: getPattern(v.patternId) }];
  });

// Opções do produto (ex.: comprimento da fita, prendedor, nome). Seleção: { [optionId]: { choiceId, text } }.
export const optionChoice = (product, optionId, choiceId) => product?.options?.find((o) => o.id === optionId)?.choices.find((c) => c.id === choiceId) ?? null;
// Opções que a escolha feita dispensa (ex.: "Só o ursinho" dispensa a fita e o prendedor).
export const hiddenOptionIds = (product, selection = {}) =>
  new Set((product?.options ?? []).flatMap((o) => optionChoice(product, o.id, selection?.[o.id]?.choiceId)?.hidesOptions ?? []));
export const optionsDelta = (product, selection = {}) =>
  Object.entries(selection ?? {}).filter(([oid]) => !hiddenOptionIds(product, selection).has(oid)).reduce((sum, [oid, v]) => sum + (optionChoice(product, oid, v?.choiceId)?.priceDelta ?? 0), 0);
// Texto pedido numa escolha: se o título fala de medidas (ex.: "Altura 94-100 cm") pede-se um número em cm, não um nome.
export const isMeasureLabel = (label) => /\b(cm|altura|medida)\b/i.test(label ?? "");
export const optionTextOk = (label, text) => (isMeasureLabel(label) ? /^\d{2,3}$/.test(text) : /^[A-Za-zÀ-ÿ' -]*$/.test(text));
// Acréscimo por pedir nome (campo "Nome do bebé"): só conta quando a cliente escreve um nome.
export const nameFee = (product, name) => ((name ?? "").trim() ? product?.personalization?.price ?? 0 : 0);
export const unitPrice = (product, selection, name = "") => (typeof product?.price === "number" ? Math.round((product.price + optionsDelta(product, selection) + nameFee(product, name)) * 100) / 100 : null);
export const defaultOptions = (product) => Object.fromEntries((product?.options ?? []).map((o) => [o.id, { choiceId: (o.choices.find((c) => c.available !== false) ?? o.choices[0]).id, text: "" }]));
export const optionLines = (product, selection) =>
  (product?.options ?? []).flatMap((o) => {
    const v = selection?.[o.id];
    if (hiddenOptionIds(product, selection).has(o.id)) return [];
    const ch = v && optionChoice(product, o.id, v.choiceId);
    return ch ? [{ label: o.label, value: ch.label + (v.text ? ` (${v.text})` : "") }] : [];
  });
// Preço "desde": o mais baixo possível (sem acréscimos opcionais).
export const hasOptionExtras = (product) => (product?.options ?? []).some((o) => o.choices.some((c) => c.priceDelta > 0));

// Coleções (campanhas como "Coleção de Natal"): independentes das categorias.
export const getCollections = () => state.collections;
export const getCollection = (slug) => state.collections.find((c) => c.slug === slug) ?? null;
export const getCollectionProducts = (collection) => (collection?.productSlugs ?? []).map(getProduct).filter(Boolean);
export const getCollectionPatterns = (collection) => (collection?.patternIds ?? []).map(getPattern).filter(Boolean);
export const collectionsOf = (product) => (product?.collections ?? []).map(getCollection).filter(Boolean);
// Destaques da homepage, por localização, pela ordem definida no painel.
export const featuredCollections = (placement) =>
  state.collections.filter((c) => c.feature?.enabled && c.feature.placement === placement).sort((a, b) => a.feature.order - b.feature.order);
